import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  decodeCampusIds,
  getCampusSlugFromCookie,
  clearCampusSlugCookie,
} from '../utils/campusFilter';

interface CampusFilterState {
  /** Decoded campus IDs from zid param (e.g. ["ZCA0007","ZCA00067"]) */
  campusIds: string[] | null;
  /** Human-readable campus slug from path (e.g. "allen") */
  campusSlug: string | null;
  /** Whether any campus filter is active */
  isFiltered: boolean;
  /** Set campus IDs directly (used after slug resolution) */
  setCampusIds: (ids: string[] | null) => void;
  /** Set campus slug (used by CampusSlugRedirect) */
  setCampusSlug: (slug: string | null) => void;
}

const CampusContext = createContext<CampusFilterState | undefined>(undefined);

export const useCampusFilter = (): CampusFilterState => {
  const ctx = useContext(CampusContext);
  if (!ctx) throw new Error('useCampusFilter must be used within CampusProvider');
  return ctx;
};

export const CampusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const [campusIds, setCampusIds] = useState<string[] | null>(null);
  const [campusSlug, setCampusSlugState] = useState<string | null>(null);

  // On mount, resolve zid from URL or cookie
  useEffect(() => {
    const zid = searchParams.get('zid');
    const decoded = decodeCampusIds(zid);
    if (decoded) {
      setCampusIds(decoded);
      return;
    }

    // Check for a persisted campus slug cookie (from /allen redirect)
    const savedSlug = getCampusSlugFromCookie();
    if (savedSlug) {
      setCampusSlugState(savedSlug);
    }
  }, [searchParams]);

  const setCampusSlug = useCallback((slug: string | null) => {
    setCampusSlugState(slug);
    if (!slug) clearCampusSlugCookie();
  }, []);

  const isFiltered = !!(campusIds && campusIds.length > 0) || !!campusSlug;

  return (
    <CampusContext.Provider
      value={{ campusIds, campusSlug, isFiltered, setCampusIds, setCampusSlug }}
    >
      {children}
    </CampusContext.Provider>
  );
};
